import { tokenStorage, parseErrorMessage } from "./apiClient";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const MAX_UPLOAD_EDGE = 1600;
const JPEG_QUALITY = 0.82;
/** Stay under common nginx 1m defaults until servers are updated. */
const TARGET_MAX_BYTES = 900_000;

async function refreshAccessToken() {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/admin/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false || !body.data?.accessToken) {
      return null;
    }
    tokenStorage.setTokens(body.data.accessToken, body.data.refreshToken);
    return body.data.accessToken;
  } catch {
    return null;
  }
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

/**
 * Downscale/compress large photos so defect uploads don't hit nginx 413
 * (which browsers report as "Failed to fetch").
 */
async function prepareUploadFile(file) {
  if (!file || !file.type?.startsWith("image/")) {
    return file;
  }
  // Keep small JPEG/WebP as-is; always convert large or PNG uploads.
  if (
    file.size <= TARGET_MAX_BYTES &&
    (file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/webp")
  ) {
    return file;
  }
  try {
    const img = await loadImageElement(file);
    const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const toJpegBlob = (q) =>
      new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", q));

    let quality = JPEG_QUALITY;
    let blob = await toJpegBlob(quality);
    while (blob && blob.size > TARGET_MAX_BYTES && quality > 0.45) {
      quality -= 0.1;
      blob = await toJpegBlob(quality);
    }
    if (!blob) return file;
    const name = (file.name || "upload").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

async function postUpload(file, folder, token) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", folder);

  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/v1/admin/uploads/image`, {
      method: "POST",
      headers,
      body: formData,
    });
  } catch {
    throw new Error(
      "Upload failed (network). If the image is large, try a smaller photo — the API gateway may still reject big files."
    );
  }

  if (response.status === 413) {
    throw new Error("Image is too large for the server. Try a smaller photo (under ~1 MB).");
  }

  const body = await response.json().catch(() => ({}));
  return { response, body };
}

export async function uploadImage(file, folder = "defect-images") {
  const prepared = await prepareUploadFile(file);
  let token = tokenStorage.getAccessToken();

  let { response, body } = await postUpload(prepared, folder, token);

  if ((response.status === 401 || response.status === 403) && tokenStorage.getRefreshToken()) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      token = newToken;
      ({ response, body } = await postUpload(prepared, folder, token));
    }
  }

  if (!response.ok || body.success === false) {
    throw new Error(parseErrorMessage(body, response.status));
  }
  return body.data;
}
